// mbscan manager-client の同名部品と共通。片側だけで変えない
import type { LucideIcon } from 'lucide-react'
import { AlertTriangle } from 'lucide-react'

import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/feedback.md

export type ErrorPanelLayout = 'screen' | 'section'

// 取得失敗・権限なし・認証切れなど「想定内のエラー」で画面（または区画）を出せないときの中央寄せカード。
// 描画中の例外を受ける RouteErrorFallback もこのカードで描画し、エラー画面の見た目を1つに揃える。
// GraphQL エラーを受けて出す場合は文言を手書きせず GraphqlErrorPanel（components/app）を使う。
// どのケースでどの文言が出るかは storybook の UI/Feedback/ErrorPanel・エラー表示ガイドで一覧できる
export function ErrorPanel({
  title,
  description,
  action,
  secondaryAction,
  layout = 'screen',
  className,
}: {
  title: string
  description?: string
  /** icon は文字のあるボタンの先頭アイコン。ラベルと合わないアイコンが出る事故を防ぐため既定値は持たず必須にしている */
  action?: { label: string; onClick: () => void; icon: LucideIcon }
  /**
   * 主ボタンの横に並べる補助リンク（「ホームへ戻る」等）。
   * router の外（RouteErrorFallback）でも使えるよう、Link ではなく素の href で遷移する
   */
  secondaryAction?: { label: string; href: string; icon: LucideIcon }
  /**
   * screen: 画面全体の中央に出す（ゲート・コンテナが画面ごと置き換えるとき）。
   * section: タブ本体・カード内など区画の中に出す。screen のまま区画に置くと画面1枚分の高さを取ってしまうため分ける
   */
  layout?: ErrorPanelLayout
  className?: string
}) {
  // 画面全体を置き換えるときはページの見出し、区画内ではその区画の見出しになる
  const Heading = layout === 'screen' ? 'h1' : 'h2'
  return (
    <div
      className={cn(
        'flex w-full items-center justify-center',
        layout === 'screen' ? 'min-h-screen bg-background p-6' : 'py-10',
        className,
      )}
    >
      <Card className="w-full max-w-md">
        {/* CardHeader は grid のため items-center では横方向に寄らない。justify-items で中央に置く */}
        <CardHeader className="justify-items-center text-center">
          <div className="mb-2 flex size-11 items-center justify-center rounded-lg border border-destructive/25 bg-destructive/5 text-destructive">
            <AlertTriangle className="size-5" aria-hidden="true" />
          </div>
          <Heading className="text-lg leading-snug font-medium">{title}</Heading>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4 text-center">
          {description !== undefined && <p className="text-sm text-muted-foreground">{description}</p>}
          {(action !== undefined || secondaryAction !== undefined) && (
            <div className="flex flex-wrap justify-center gap-3">
              {action !== undefined && (
                <Button type="button" onClick={action.onClick}>
                  <action.icon data-icon="inline-start" />
                  {action.label}
                </Button>
              )}
              {secondaryAction !== undefined && (
                <a href={secondaryAction.href} className={buttonVariants({ variant: 'outline' })}>
                  <secondaryAction.icon data-icon="inline-start" />
                  {secondaryAction.label}
                </a>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
